/**
 * Enregistre le Service Worker Nestor — Phase 3, incrément 3.1.
 *
 * Pourquoi un module séparé ?
 *   - Garde `main.ts` minimal et lisible.
 *   - Permet d'isoler les checks (feature detection, mode prod) et de
 *     les tester séparément.
 *   - Phase 3.2 enrichira ce module avec la détection d'update + le
 *     dispatch d'événements vers l'UI (UpdateBanner).
 *
 * Stratégie : on enregistre `/sw.js` au scope racine `/`. Le SW
 * lui-même vit dans `apps/web/public/sw.js` — Vite sert tout le
 * répertoire `public/` à la racine sans bundling.
 *
 * Garde-fous :
 *   - Feature detection (`'serviceWorker' in navigator`).
 *   - Production-only (`import.meta.env.PROD`) pour éviter que le
 *     dev server Vite ne se mélange avec un SW qui cache le HTML —
 *     HMR fonctionne sans SW, et les `/assets/` n'existent qu'en
 *     build. En dev, retourne immédiatement.
 *   - Erreurs silencieuses (warn) — un SW qui rate ne doit pas
 *     casser le boot de l'app.
 */

/**
 * Lance l'enregistrement du SW au prochain `load` (pour ne pas
 * compétitionner avec le rendu initial). Idempotent : appel multiple
 * = no-op après le premier (le navigateur dédoublonne).
 *
 * Retourne une promise pour permettre aux tests (Phase 3.2) d'attendre
 * l'enregistrement. En prod le caller (main.ts) ignore le résultat.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  if (!import.meta.env.PROD) {
    return null;
  }

  return new Promise((resolve) => {
    // `load` event = quand le HTML + assets initiaux sont chargés. On
    // n'enregistre pas avant pour ne pas ralentir le first paint.
    const tryRegister = async (): Promise<void> => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });
        // Hook Phase 3.2 : detect new SW waiting → dispatch event
        // que l'UI capture pour afficher le bandeau de mise à jour.
        if (registration.waiting) {
          notifyUpdateAvailable(registration);
        }
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              // Il y avait déjà un SW actif → c'est une mise à jour.
              notifyUpdateAvailable(registration);
            }
          });
        });
        resolve(registration);
      } catch (err) {
        console.warn('[nestor:sw] registration failed:', err);
        resolve(null);
      }
    };

    if (document.readyState === 'complete') {
      void tryRegister();
    } else {
      window.addEventListener('load', () => void tryRegister(), { once: true });
    }
  });
}

/**
 * Nom de l'event custom dispatché à `window` quand le SW détecte
 * qu'une nouvelle version est prête à prendre le relais. L'UI
 * (`ui/UpdateBanner.ts`) y souscrit pour afficher un bandeau
 * « Mise à jour disponible — recharger ? ».
 *
 * Detail : `{ registration }` — l'UI s'en sert pour envoyer
 * `SKIP_WAITING` au SW waiting via `registration.waiting.postMessage`.
 */
export const SW_UPDATE_EVENT = 'nestor:sw-update';

export interface SwUpdateEventDetail {
  registration: ServiceWorkerRegistration;
}

/**
 * Notifie l'UI qu'une nouvelle version du SW est prête. Phase 3.2 :
 * dispatch un `CustomEvent` à `window`. L'UI bannière qui écoute
 * `SW_UPDATE_EVENT` propose le rechargement à l'utilisateur.
 */
function notifyUpdateAvailable(registration: ServiceWorkerRegistration): void {
  console.info('[nestor:sw] update available — reload to apply');
  if (typeof window === 'undefined') return;
  const event = new CustomEvent<SwUpdateEventDetail>(SW_UPDATE_EVENT, {
    detail: { registration },
  });
  window.dispatchEvent(event);
}

/**
 * @internal pour les tests : permet de simuler une registration sans
 * passer par la window event loop.
 */
export async function _registerForTests(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    return null;
  }
}
